import * as React from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Paper,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";

export const AWARE_THREAT_SELECTION_KEY = "tk.awareThreatSelections";

export default function AwareThreatWallScreen(props: {
  handlePageChange: (page: string) => void;
  nextPage?: string;
  backPage?: string;
}) {
  const { lang } = useI18n();
  const L = (en: string, de: string) => (lang === "de" ? de : en);
  const nextPage = props.nextPage ?? "OthersSaid";
  const backPage = props.backPage ?? "VibeQuestions:aware";

  /* ---------------- STATE ---------------- */

  const [threats, setThreats] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [workshopId, setWorkshopId] = React.useState<string | null>(null);
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  /* ---------------- LOAD INITIAL DATA ---------------- */

  React.useEffect(() => {
    loadInitialData();
  }, []);

  async function loadInitialData() {
    try {
      setLoading(true);

      // 1️⃣ active workshop
      const { data: workshop } = await supabase
        .from("workshops")
        .select("id")
        .eq("is_active", true)
        .single();

      if (workshop) setWorkshopId(workshop.id);

      // 2️⃣ current user → employee
      const { data: userRes } = await supabase.auth.getUser();

      if (userRes?.user?.id) {
        const { data: emp } = await supabase
          .from("employees")
          .select("id")
          .eq("auth_user_id", userRes.user.id)
          .single();

        if (emp) setEmployeeId(emp.id);
      }

      // 3️⃣ load threats
      const { data: threatData } = await supabase
        .from("threats")
        .select("id, label_en, label_de")
        .order("label_en");

      if (threatData) setThreats(threatData);

    } catch (err) {
      console.error("INIT ERROR:", err);
    } finally {
      setLoading(false);
    }
  }

  /* ---------------- TOGGLE ---------------- */

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((v) => v !== id)
        : [...prev, id]
    );
  }

  /* ---------------- SAVE ---------------- */

  async function handleContinue() {
    sessionStorage.setItem(AWARE_THREAT_SELECTION_KEY, JSON.stringify(selected));

    if (!workshopId || !employeeId) {
      console.error("Missing workshop or employee");
      props.handlePageChange(nextPage);
      return;
    }

    try {
      /* ✅ STEP 1: ensure participant exists */
      const { data: participant } = await supabase
        .from("workshop_participants")
        .select("employee_id")
        .eq("workshop_id", workshopId)
        .eq("employee_id", employeeId)
        .maybeSingle();

      if (!participant) {
        props.handlePageChange(nextPage);
        return;
      }

      /* ✅ STEP 2: delete old selections */
      await supabase
        .from("workshop_participant_threats")
        .delete()
        .eq("workshop_id", workshopId)
        .eq("employee_id", employeeId);

      /* ✅ STEP 3: insert new selections */
      const inserts = selected.map((threatId) => ({
        workshop_id: workshopId,
        employee_id: employeeId,
        threat_id: threatId,
      }));

      const { error } = await supabase
        .from("workshop_participant_threats")
        .insert(inserts);

      if (error) {
        console.error("INSERT ERROR:", error);
        props.handlePageChange(nextPage);
        return;
      }

      /* ✅ STEP 4: continue */
      props.handlePageChange(nextPage);

    } catch (err) {
      console.error("SAVE ERROR:", err);
      props.handlePageChange(nextPage);
    }
  }

  /* ---------------- UI ---------------- */

  if (loading) {
    return (
      <Box sx={{ color: "#fff", textAlign: "center", mt: 10 }}>
        Loading...
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/threats.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 3, sm: 5, md: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
        }}
      >

        {/* QUESTION */}
        <Typography
          sx={{
            mb: 4,
            color: "#fff",
            fontSize: { xs: "1.4rem", sm: "1.7rem" },
            fontWeight: 700,
          }}
        >
          {L(
            "These 25 threats differ in impact, probability and speed - which ones do you consider the most likely?",
            "Diese 25 Bedrohungslagen unterscheiden sich nach Schaden, Wahrscheinlichkeit und Geschwindigkeit - welche sind für dich am wahrscheinlichsten?"
          )}
        </Typography>

        {/* OPTIONS */}
        <Stack>
          {threats.map((t) => {
            const label = L(t.label_en, t.label_de);
            const checked = selected.includes(t.id);

            return (
              <FormControlLabel
                key={t.id}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => toggle(t.id)}
                    sx={{
                      color: "rgba(255,255,255,0.5)",
                      "&.Mui-checked": { color: "#f05969" },
                    }}
                  />
                }
                label={label}
                sx={{
                  color: "#fff",
                  mx: 0,
                  px: 2,
                  py: 1.2,
                  borderRadius: 2.5,
                  mb: 1,
                  border: "1px solid rgba(255,255,255,0.18)",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.09)",
                  },
                }}
              />
            );
          })}
        </Stack>

        {/* ACTIONS */}
        <Stack direction="row" spacing={2} sx={{ mt: 5 }}>
          <Button
            variant="outlined"
            onClick={() => {
              sessionStorage.removeItem("tk.awareThreatWallResumeIndex");
              props.handlePageChange(backPage);
            }}
            sx={{
              borderColor: "rgba(255,255,255,0.4)",
              color: "#fff",
            }}
          >
            {L("Back", "Zurück")}
          </Button>

          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={selected.length === 0}
            sx={{
              background: "linear-gradient(90deg, #b50d20, #d6293e)",
              fontWeight: 700,
            }}
          >
            {L("Continue", "Weiter")}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
